<template>
  <UModal
    v-model="model"
    :ui="{
      wrapper: 'relative z-50 ',
      margin: 'sm:my-8 sm:mx-2.5',
      width: `${sizeModal[size]}`,
      background: 'bg-hui-background dark:bg-hui-background',
      rounded: '!rounded-none',
      overlay: { background: 'bg-stone-950/75 dark:bg-stone-950/75' },
      container: 'items-center min-h-dvh',
      fullscreen: 'w-dvw h-dvh',
    }"
    :fullscreen="fullscreen"
    class="default-modal"
    @update:model-value="handleModelUpdate"
  >
    <div class="default-modal__wrapper">
      <UButton
        v-if="showBackButton"
        :ui="{
          icon: {
            base: 'flex-shrink-0 font-semibold',
            size: {
              '2xs': 'h-4 w-4',
              xs: 'h-4 w-4',
              sm: 'h-5 w-5',
              md: 'h-5 w-5',
              lg: 'h-5 w-5',
              xl: 'h-7 w-7',
            },
          },
        }"
        color="gray"
        variant="ghost"
        icon="i-heroicons-arrow-left-16-solid"
        class="default-modal__back"
        size="xl"
        @click="handleBackClick"
      />
      <UButton
        :ui="{
          icon: {
            base: 'flex-shrink-0 font-semibold',
            size: {
              '2xs': 'h-4 w-4',
              xs: 'h-4 w-4',
              sm: 'h-5 w-5',
              md: 'h-5 w-5',
              lg: 'h-5 w-5',
              xl: 'h-7 w-7',
            },
          },
        }"
        color="gray"
        variant="ghost"
        icon="i-heroicons-x-mark-16-solid"
        class="default-modal__close"
        size="xl"
        @click="emit('update:modelValue', false)"
      />
      <div v-if="title || $slots.title" class="default-modal__title">
        <slot name="title">
          <h3>{{ title }}</h3>
        </slot>
      </div>
      <div class="default-modal__body">
        <slot name="body">
          {{ text }}
        </slot>
      </div>
    </div>
  </UModal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { consola } from 'consola';
import { App as CapApp } from '@capacitor/app';

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    default: undefined,
  },
  text: {
    type: String,
    default: undefined,
  },
  size: {
    type: String,
    default: 's',
  },
  fullscreen: {
    type: Boolean,
    required: false,
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'back']);

// Добавляем логирование для отладки
onMounted(() => {
  consola.log('🎭 DefaultModal: Component mounted');
  consola.log('📊 DefaultModal: modelValue:', props.modelValue);
  consola.log('📏 DefaultModal: size:', props.size);
  consola.log('📱 DefaultModal: fullscreen:', props.fullscreen);
});

const handleModelUpdate = (value: boolean) => {
  consola.log('🔄 DefaultModal: model-value updated:', value);
  emit('update:modelValue', value);
};

const sizeModal = ref({
  xl: 'sm:max-w-[1100px]',
  l: 'sm:max-w-[992px]',
  m: 'sm:max-w-[880px]',
  s: 'sm:max-w-[608px]',
});

const model = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const handleBackClick = () => {
  emit('back'); // Эмитим событие назад
};

// Сохраняем ссылку на обработчик для корректного удаления
let backButtonHandler: ((event: any) => void) | null = null;

// Реагируем на аппаратную кнопку назад (или жест назад) на Android/iOS через Capacitor
function onCapacitorBackButton(event: any) {
  consola.log('🔙 DefaultModal: Capacitor backButton pressed');
  // Модалка закрывается только если открыта
  if (props.modelValue) {
    consola.log('🔙 DefaultModal: Closing modal via back button');
    emit('update:modelValue', false);
    // Предотвращаем дальнейшее выполнение других обработчиков
    event?.preventDefault?.();
  }
}

onMounted(() => {
  consola.log('🔙 DefaultModal: Registering backButton listener');
  backButtonHandler = onCapacitorBackButton;
  CapApp.addListener('backButton', backButtonHandler);
});

onBeforeUnmount(() => {
  consola.log('🔙 DefaultModal: Removing backButton listener');
  if (backButtonHandler) {
    CapApp.removeListener('backButton', backButtonHandler);
    backButtonHandler = null;
  }
});
</script>

<style scoped lang="scss">
.default-modal {
  &__wrapper {
    @apply relative pt-[var(--safe-area-inset-top)] ;
  }

  // Выше слоёв карточки товара (ProductSettingsSheet / ProductActions — z-index 52)
  &__close {
    @apply absolute top-[calc(1.25rem+var(--safe-area-inset-top))] right-3 lg:top-5 lg:right-5 w-12 h-12 bg-white z-[60] text-black rounded-full hover:rotate-90;
  }

  &__back {
    @apply absolute top-[calc(1.25rem+var(--safe-area-inset-top))] left-3 lg:top-5 lg:left-5 w-12 h-12 bg-white z-[60] text-black rounded-full;
  }

  &__title {
    @apply justify-items-center text-center mb-8 gap-4;
  }

  &__body{
    @apply max-lg:h-[calc(100dvh-0.625rem-var(--safe-area-inset-top))] relative;
  }
}
</style>